import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Eraser, Loader2, RotateCcw, AlertTriangle, Archive, Trash2 } from "lucide-react";

const CONFIRM_WORD = "REMISE A ZERO";
const PURGE_WORD = "EFFACER TOUT";
const COLL_LABEL = {
  donations: "Dons", withdrawals: "Retraits", wallet_transactions: "Journal des portefeuilles", revenue_transactions: "Journal des revenus",
  ad_credit_transactions: "Mouvements de crédits pub", ad_credit_purchases: "Achats de crédits pub", subscriptions: "Anciens abonnements", verified_subscriptions: "Abonnements Yalola Vérifié",
  recurring_donations: "Offrandes récurrentes", ads: "Publicités payées", expenses: "Dépenses", "users.balances": "Soldes remis à 0",
};
const fmtMoney = (n, c) => (c && c !== "?" ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: c }).format(n || 0) : `${(n || 0).toLocaleString("fr-FR")} (sans devise)`);
const fmtSums = (sums) => Object.entries(sums || {}).map(([c, v]) => fmtMoney(v, c)).join(" · ") || "—";
const fmtDate = (s) => (s ? new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");

// Admin › Comptabilité (super admin) — « Remise à zéro financière » : archive (restaurable) de toutes les
// données financières de test/démo + soldes à 0, pour repartir d'un tableau propre en production.
export const FinanceResetCard = ({ onDone }) => {
  const [open, setOpen] = useState(false);       // "archive" | "purge" | false
  const [lastPurge, setLastPurge] = useState(null);
  const loadPurge = () => api.get("/admin/finance/reset/inventory?scope=all").then(({ data }) => setLastPurge(data.last_purge || null)).catch(() => {});
  useEffect(() => { loadPurge(); }, []);
  const [batches, setBatches] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const loadBatches = () => api.get("/admin/finance/reset/batches").then(({ data }) => setBatches(data.batches || [])).catch(() => setBatches([]));
  useEffect(() => { loadBatches(); }, []);
  const active = (batches || []).filter((b) => !b.restored_at);
  const last = (batches || [])[0] || null;

  const restore = async () => {
    const id = restoring; if (!id) return;
    try {
      const { data } = await api.post(`/admin/finance/reset/${id}/restore`);
      toast.success(`Lot restauré (${Object.values(data.restored || {}).reduce((a, b) => a + b, 0)} document(s))`);
      setRestoring(null); loadBatches(); onDone?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Restauration impossible"); }
  };

  return (
    <div className="yalola-card p-4 space-y-3" data-testid="finance-reset-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Eraser size={17} /></span>
          <div className="min-w-0">
            <div className="text-sm font-semibold">Remise à zéro financière</div>
            <p className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
              Archive (sans rien supprimer) tous les dons, retraits, journaux, crédits pub, abonnements, pubs payées et dépenses de la période de test, et remet les soldes à 0. Chaque lot reste restaurable.
            </p>
            {last && (
              <p className="text-xs mt-1" data-testid="finance-reset-last">
                Dernière remise à zéro : <b>{fmtDate(last.at)}</b> · {last.total} document(s) · {fmtSums(last.sums)}{last.restored_at ? ` · restaurée le ${fmtDate(last.restored_at)}` : ""}
              </p>
            )}
            {lastPurge && (
              <p className="text-xs mt-1" style={{ color: "#B91C1C" }} data-testid="finance-purge-last">
                Dernier effacement définitif (fin des tests) : <b>{fmtDate(lastPurge.at)}</b> · {lastPurge.total} élément(s) · {fmtSums(lastPurge.sums)}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen("archive")} data-testid="finance-reset-open">
            <Eraser size={13} className="mr-1" /> Aperçu & remise à zéro
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" style={{ borderColor: "#B91C1C", color: "#B91C1C" }} onClick={() => setOpen("purge")} data-testid="finance-purge-open">
            <Trash2 size={13} className="mr-1" /> Tout effacer (fin des tests)
          </Button>
        </div>
      </div>
      {active.length > 0 && (
        <div className="rounded-lg border divide-y text-xs" style={{ borderColor: "var(--yalola-border)" }} data-testid="finance-reset-batches">
          {active.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`finance-reset-batch-${b.id}`}>
              <span className="flex items-center gap-1.5 min-w-0"><Archive size={13} style={{ color: "var(--yalola-text-muted)" }} /> {fmtDate(b.at)} · {b.scope === "test_only" ? "données de test" : "tout"} · {b.total} document(s) · {fmtSums(b.sums)}{b.note ? ` · ${b.note}` : ""}</span>
              <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => setRestoring(b.id)} data-testid={`finance-reset-restore-${b.id}`}><RotateCcw size={12} className="mr-1" /> Restaurer</Button>
            </div>
          ))}
        </div>
      )}
      {open && <FinanceResetDialog mode={open} onClose={() => setOpen(false)} onDone={() => { loadBatches(); loadPurge(); onDone?.(); }} />}
      <ConfirmDialog open={!!restoring} title="Restaurer ce lot ?" description="Les documents archivés et les anciens soldes reviennent dans la comptabilité. Cette action est journalisée." confirmLabel="Restaurer" onConfirm={restore} onCancel={() => setRestoring(null)} testId="finance-reset-restore-confirm" />
    </div>
  );
};

// mode "archive" : archive restaurable (vraies données) · mode "purge" : suppression DÉFINITIVE (fin de la période de test).
const FinanceResetDialog = ({ mode = "archive", onClose, onDone }) => {
  const purge = mode === "purge";
  const word = purge ? PURGE_WORD : CONFIRM_WORD;
  const [scope, setScope] = useState("all");
  const [inv, setInv] = useState(null);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState("");
  const [includeArchives, setIncludeArchives] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setInv(null);
    api.get(`/admin/finance/reset/inventory?scope=${purge ? "all" : scope}`).then(({ data }) => setInv(data)).catch(() => setInv({ error: true }));
  }, [scope, purge]);
  const ready = confirm.trim().toUpperCase() === word && inv && !inv.error && !busy;
  const extra = purge ? (inv?.money_notifications || 0) + (inv?.iap_events || 0) + (includeArchives ? (inv?.archives?.docs || 0) + (inv?.archives?.batches || 0) : 0) : 0;
  const total = (inv?.total_docs || 0) + (inv?.balances?.count || 0) + extra;

  const run = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      if (purge) {
        const { data } = await api.post("/admin/finance/purge", { confirm: confirm.trim(), note, include_archives: includeArchives });
        toast.success(`Effacement définitif effectué : ${data.purge?.total || 0} élément(s) supprimé(s), tous les soldes à 0`);
      } else {
        const { data } = await api.post("/admin/finance/reset", { confirm: confirm.trim(), scope, note });
        toast.success(`Remise à zéro effectuée : ${data.batch?.total || 0} document(s) archivé(s)`);
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.friendlyMessage || (purge ? "Effacement impossible" : "Remise à zéro impossible")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent variant="sheet" className="sm:max-w-2xl" data-testid={purge ? "finance-purge-dialog" : "finance-reset-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display">{purge ? "Tout effacer — fin de la période de test" : "Remise à zéro financière"}</DialogTitle>
          <DialogDescription>{purge ? `Aperçu de ce qui sera supprimé DÉFINITIVEMENT, puis confirmation en tapant « ${word} ».` : `Aperçu de ce qui sera archivé, puis confirmation en tapant « ${word} ».`}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 text-sm">
          {purge ? (
            <p className="flex items-start gap-2 rounded-lg p-3 text-xs" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="finance-purge-warning">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span><b>Aucune archive, aucune restauration possible.</b> Toutes les données financières de test (dons, retraits, journaux, crédits pub et leurs achats, abonnements, pubs payées, dépenses) sont supprimées, les <b>portefeuilles et crédits pub de tous les membres passent à 0</b>, et les notifications d'argent de test disparaissent. À utiliser une seule fois, avant les premières vraies données — ensuite, utilise « Archive et remise à zéro » (restaurable).</span>
            </p>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {[["all", "Tout (bascule en production)"], ["test_only", "Données de test seulement"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setScope(k)} className={`pill text-xs ${scope === k ? "pill-primary" : "pill-outline"}`} data-testid={`finance-reset-scope-${k}`}>{l}</button>
              ))}
            </div>
          )}
          {inv === null && <div className="py-8 grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>}
          {inv?.error && <p className="text-red-600" data-testid="finance-reset-error">Impossible de charger l'aperçu.</p>}
          {inv && !inv.error && (
            <>
              {inv.stripe_mode === "live" && (
                <p className="flex items-start gap-2 rounded-lg p-3 text-xs" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="finance-reset-warning-live">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" /> Stripe est en mode LIVE : vérifie qu'aucune ligne ci-dessous n'est un vrai paiement avant de continuer.
                </p>
              )}
              {inv.accounts_not_test_like > 0 && (
                <p className="flex items-start gap-2 rounded-lg p-3 text-xs" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid="finance-reset-warning-accounts">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {inv.accounts_not_test_like} compte(s) concerné(s) ne sont pas marqués « test » (ex. l'équipe interne). Leurs lignes seront {purge ? "supprimées" : "archivées"} comme les autres.
                </p>
              )}
              {(inv.badge_losers || []).length > 0 && (
                <p className="flex items-start gap-2 rounded-lg p-3 text-xs" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="finance-reset-warning-badges">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Badge Yalola Vérifié issu d'un abonnement de test — il tombera après {purge ? "l'effacement" : "la remise à zéro"} : <b>{inv.badge_losers.map((u) => u.name || u.email).join(", ")}</b>. À ré-attribuer si besoin via Dons · Vérifié · Crédits pub › Yalola Vérifié. Les badges des évaluateurs et créateurs KYC ne sont pas touchés.</span>
                </p>
              )}
              <div className="rounded-lg border divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="finance-reset-inventory">
                {Object.entries(inv.collections || {}).map(([coll, c]) => (
                  <div key={coll} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`finance-reset-row-${coll}`} data-count={c.count}>
                    <span className="font-medium">{COLL_LABEL[coll] || coll}</span>
                    <span style={{ color: c.count ? "var(--yalola-text)" : "var(--yalola-text-muted)" }}>{c.count} document(s){c.count ? ` · ${fmtSums(c.by_currency)}` : ""}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid="finance-reset-balances" data-count={inv.balances?.count || 0}>
                  <span className="font-medium">{COLL_LABEL["users.balances"]}</span>
                  <span>{inv.balances?.count || 0} compte(s) · portefeuilles {(inv.balances?.wallet_total || 0).toLocaleString("fr-FR")} · crédits pub {(inv.balances?.ad_credit_total || 0).toLocaleString("fr-FR")}</span>
                </div>
              </div>
              {purge && (
                <div className="rounded-lg border divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="finance-purge-extras">
                  <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid="finance-purge-row-notifications" data-count={inv.money_notifications || 0}>
                    <span className="font-medium">Notifications d'argent (soutien reçu, retrait…)</span><span>{inv.money_notifications || 0} notification(s)</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid="finance-purge-row-iap" data-count={inv.iap_events || 0}>
                    <span className="font-medium">Journal des achats in-app (sandbox)</span><span>{inv.iap_events || 0} événement(s)</span>
                  </div>
                  <label className="flex items-center justify-between gap-3 px-3 py-2 text-xs cursor-pointer" data-testid="finance-purge-row-archives" data-count={inv.archives?.docs || 0}>
                    <span className="flex items-center gap-2 font-medium">
                      <input type="checkbox" checked={includeArchives} onChange={(e) => setIncludeArchives(e.target.checked)} data-testid="finance-purge-include-archives" />
                      Supprimer aussi les archives des remises à zéro précédentes
                    </span>
                    <span>{inv.archives?.batches || 0} lot(s) · {inv.archives?.docs || 0} document(s)</span>
                  </label>
                </div>
              )}
              <p className="text-xs font-semibold" data-testid="finance-reset-total">{total} élément(s) seront {purge ? "supprimés définitivement" : "archivés"} — la comptabilité repart à 0,00.</p>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={purge ? "Note (facultatif) — ex. Fin des tests, passage en production" : "Note (facultatif) — ex. Bascule production 09/2026"} maxLength={300} data-testid="finance-reset-note" />
              <div className="space-y-1">
                <label className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Tape <b>{word}</b> pour confirmer</label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={word} autoComplete="off" data-testid="finance-reset-confirm-input" />
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={busy} data-testid="finance-reset-cancel">Annuler</Button>
          <Button className="rounded-full" style={{ background: "#B91C1C" }} onClick={run} disabled={!ready || total === 0} data-testid="finance-reset-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : (purge ? <Trash2 size={14} className="mr-1" /> : <Eraser size={14} className="mr-1" />)} {purge ? "Effacer définitivement" : "Archiver et remettre à zéro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FinanceResetCard;
